import React from 'react';
import _ from 'lodash';

import 'moment-timezone';
import IAB_CATEGORIES from 'common/constants/iab-categories';
import { hasTopLevelFieldsBeenModified } from 'widgets/history-table';

import { formatNumber_percentage, formatNumber_currency } from 'utils/formatting';

import { formatCurrency } from 'common/fns';
import { isInternalUser } from 'states/profile/business-rules';
import {
    CampaignAutoBudgetAllocationOptions,
    getCampaignInventoryTypeOptions,
    getCampaignTypeOptions,
    isUsingGuaranteedInventoryType,
    RevenueModelMapping,
    canUseProgrammaticGuaranteedCampaigns,
} from 'states/resources/campaigns/business-logic';

/**
 * Formats a single campaign client for history display.
 *
 * Resolves the organization name from the lookup list and appends "(Shared)"
 * when the client is shared, so sharing-only changes produce distinct From/To
 * values in the history diff.
 *
 * @param {{ organization: string, shared?: boolean }} client - Client from a history snapshot
 * @param {{ id: string, name: string }[]} organizations - Organization lookup from campaign history state
 * @returns {string} Display label, e.g. "Acme Corp (Shared)"
 */
function formatClientDisplay(client, organizations) {
    const name =
        _.get(
            _.find(organizations, organization => organization.id === client.organization),
            'name'
        ) || `Deleted (organization id: ${client.organization})`;

    return client.shared ? `${name} (Shared)` : name;
}

/**
 * Returns display labels for all clients of a given type on a history entry.
 *
 * @param {{ snapshot: { clients?: Array<{ organization: string, type: string, shared?: boolean }> } }} entry - History entry
 * @param {{ id: string, name: string }[]} organizations - Organization lookup from campaign history state
 * @param {'partner' | 'advertiser' | 'sales_rep'} clientType - Client role to include
 * @returns {string[]} Plain array of formatted client labels (never a lodash chain)
 */
function getClientNamesByType(entry, organizations, clientType) {
    return _(entry.snapshot.clients || [])
        .filter(client => client.type === clientType)
        .map(client => formatClientDisplay(client, organizations))
        .value();
}

/**
 * Whether a history patch modified clients of a specific type.
 *
 * Campaign client patches use indexed keys (e.g. `clients.1.shared`). A change
 * to one client type must not mark Partners, Advertisers, and Sales Reps as
 * all changed in the sidebar and detail panel.
 *
 * @param {{ patch: Object<string, { value: *, op: string }>, snapshot: { clients?: Array<{ type: string }> } }} entry - History entry
 * @param {'partner' | 'advertiser' | 'sales_rep'} clientType - Client role to check
 * @returns {boolean} True when the patch affects at least one client of this type
 *
 * @example
 * // Sales rep sharing toggled — only sales_rep returns true
 * hasClientTypeChange({ patch: { 'clients.1.shared': { op: 'replace' } }, snapshot: { clients: [{ type: 'advertiser' }, { type: 'sales_rep' }] } }, 'sales_rep'); // true
 * hasClientTypeChange({ patch: { 'clients.1.shared': { op: 'replace' } }, snapshot: { clients: [{ type: 'advertiser' }, { type: 'sales_rep' }] } }, 'advertiser'); // false
 */
function hasClientTypeChange(entry, clientType) {
    const patch = entry.patch || {};

    if (!hasTopLevelFieldsBeenModified(patch, ['clients'])) {
        return false;
    }

    return _.some(patch, (value, key) => {
        if (key === 'clients') {
            return _.some(entry.snapshot.clients || [], client => client.type === clientType);
        }

        const match = key.match(/^clients\.(\d+)(?:\.|$)/);
        if (!match) {
            return false;
        }

        const clientIndex = Number(match[1]);
        const client = entry.snapshot.clients?.[clientIndex];

        return client?.type === clientType;
    });
}

/**
 * On-screen Total Cost Budget labels, one string per row.
 * A single budget value stays one line. Agency margin can add Revenue Budget.
 *
 * @param {Object} snapshot - Campaign history snapshot.
 * @param {Object} campaign - Live campaign, used for guaranteed inventory.
 * @returns {string[]}
 */
function getTotalCostBudgetLabels(snapshot, campaign) {
    const revenueModel = snapshot?.revenueModel;
    const usesMaxTotalSpendLocal = [
        RevenueModelMapping.Disabled,
        RevenueModelMapping.CPMCPC,
        RevenueModelMapping.CPM,
        RevenueModelMapping.TotalSpendMarkup,
    ].includes(revenueModel);

    if (usesMaxTotalSpendLocal) {
        const totalCostBudget =
            snapshot.campaign_max_total_spend_local === 0
                ? 'Unlimited'
                : formatNumber_currency(snapshot.campaign_max_total_spend_local);

        return [`${totalCostBudget}`];
    }

    if (revenueModel === RevenueModelMapping.AgencyMargin) {
        const labels = [`Agency Margin: ${formatNumber_percentage(snapshot.billing_rate)}`];

        // this field is not used for guaranteed inventory type
        if (!isUsingGuaranteedInventoryType(campaign)) {
            labels.push(`Revenue Budget: ${formatNumber_currency(snapshot.total_billings_local)}`);
        }

        return labels;
    }

    return [];
}

/**
 * Visible Foot Traffic Attribution lines for a campaign history row.
 * Disabled is one line. Enabled adds Level, and campaign level adds Location List.
 *
 * @param {Object} snapshot - Campaign history snapshot.
 * @param {Object<string, string>} ftaLocationListsMapping - Location list id to name.
 * @returns {string}
 */
function getCampaignFtaClipboardText(snapshot, ftaLocationListsMapping) {
    const enabled = Boolean(snapshot && snapshot.fta_enabled);
    const lines = [enabled ? 'Enabled' : 'Disabled'];

    if (!enabled) {
        return lines.join('\n');
    }

    lines.push(`Level: ${snapshot.fta_management_level === 'campaign' ? 'Campaign' : 'Ad'}`);

    if (snapshot.fta_management_level === 'campaign') {
        const locationName =
            ftaLocationListsMapping && ftaLocationListsMapping[snapshot.fta_location_list];
        lines.push(locationName ? `Location List: ${locationName}` : 'Location List:');
    }

    return lines.join('\n');
}

function getFields({ storeState, campaign, ftaLocationListsMapping }) {
    const fields = [].concat(
        [
            {
                label: 'Basic',
                isCategory: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'name',
                        'default_timezone',
                        'notes',
                        'custom_fields',
                        'type',
                        'inventory_type',
                    ]);
                },
                format() {
                    return '';
                },
            },
            {
                label: 'Name',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['name']);
                },
                format(entry) {
                    return <>{entry.snapshot.name}</>;
                },
                getClipboardText(entry) {
                    return entry.snapshot.name;
                },
            },
            {
                label: 'Campaign Type',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['type']);
                },
                format(entry) {
                    return (
                        _.find(
                            getCampaignTypeOptions(),
                            ({ value }) => value === entry.snapshot.type
                        )?.label || entry.snapshot.type
                    );
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
            {
                label: 'Inventory Type',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['inventory_type']);
                },
                format(entry) {
                    return (
                        _.find(
                            getCampaignInventoryTypeOptions(),
                            ({ value }) => value === entry.snapshot.inventory_type
                        )?.label || entry.snapshot.inventory_type
                    );
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
            {
                label: 'Status',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['paused']);
                },
                format(entry) {
                    return entry.snapshot.paused ? 'Off' : 'On';
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
        ],
        _.map(campaign.custom_fields, (campaignCustomField, customFieldIndex) => {
            return {
                label: campaignCustomField.name,
                isCategory: false,
                hasChange(entry) {
                    let hasChange;
                    const isAdd = _.get(entry, 'patch.custom_fields.op') === 'add';
                    if (isAdd) {
                        hasChange = true;
                    } else {
                        const indexesOfChangedCustomFields = _(entry.patch)
                            .map((value, key) => key)
                            .filter(key => {
                                if (key.indexOf('custom_fields') > -1) {
                                    return true;
                                } else {
                                    return false;
                                }
                            })
                            .map(key => String(key.split('.')[1]))
                            .value();

                        hasChange = _.includes(
                            indexesOfChangedCustomFields,
                            String(customFieldIndex)
                        );
                    }
                    return hasChange;
                },
                format(entry) {
                    const customField = _.find(entry.snapshot.custom_fields, {
                        name: campaignCustomField.name,
                    });

                    return <>{customField ? customField.value : ''}</>;
                },
                getClipboardText(entry) {
                    const customField = _.find(entry.snapshot.custom_fields, {
                        name: campaignCustomField.name,
                    });

                    return customField ? customField.value : '';
                },
            };
        }),
        [
            {
                label: 'Default Timezone',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['default_timezone']);
                },
                format(entry) {
                    return entry.snapshot.default_timezone;
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
            {
                label: 'Notes',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['notes']);
                },
                format(entry) {
                    return <>{entry.snapshot.notes}</>;
                },
                getClipboardText(entry) {
                    return entry.snapshot.notes;
                },
            },
            {
                label: 'Categorization',
                isCategory: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'clients',
                        'advertiser_domain',
                        'iab_categories',
                    ]);
                },
                format() {
                    return '';
                },
            },
            // Partners, Advertisers, and Sales Reps share the `clients` patch prefix
            // but must be detected and formatted per role (including shared status).
            {
                label: 'Partners',
                isCategory: false,
                hasChange(entry) {
                    return hasClientTypeChange(entry, 'partner');
                },
                format(entry) {
                    const organizations = _.get(storeState, 'campaignHistory.organizations');
                    return getClientNamesByType(entry, organizations, 'partner');
                },
                getClipboardText(entry) {
                    return getClientNamesByType(
                        entry,
                        _.get(storeState, 'campaignHistory.organizations'),
                        'partner'
                    ).join(', ');
                },
            },
            {
                label: 'Advertisers',
                isCategory: false,
                hasChange(entry) {
                    return hasClientTypeChange(entry, 'advertiser');
                },
                format(entry) {
                    const organizations = _.get(storeState, 'campaignHistory.organizations');
                    return getClientNamesByType(entry, organizations, 'advertiser');
                },
                getClipboardText(entry) {
                    return getClientNamesByType(
                        entry,
                        _.get(storeState, 'campaignHistory.organizations'),
                        'advertiser'
                    ).join(', ');
                },
            },
            {
                label: 'Advertiser Domain',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['advertiser_domain']);
                },
                format(entry) {
                    return <>{entry.snapshot.advertiser_domain}</>;
                },
                getClipboardText(entry) {
                    return entry.snapshot.advertiser_domain;
                },
            },
            {
                label: 'Advertiser Category',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['iab_categories']);
                },
                format(entry) {
                    const iabCategories = _(entry.snapshot.iab_categories)
                        .map(category => IAB_CATEGORIES[category])
                        .join(', ');

                    return iabCategories;
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
            {
                label: 'Sales Reps',
                isCategory: false,
                hasChange(entry) {
                    return hasClientTypeChange(entry, 'sales_rep');
                },
                format(entry) {
                    const organizations = _.get(storeState, 'campaignHistory.organizations');
                    return getClientNamesByType(entry, organizations, 'sales_rep');
                },
                getClipboardText(entry) {
                    return getClientNamesByType(
                        entry,
                        _.get(storeState, 'campaignHistory.organizations'),
                        'sales_rep'
                    ).join(', ');
                },
            },
            {
                label: 'Budget & Billing',
                isCategory: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'currency',
                        'billing_rate',
                        'billing_term',
                        'billing_enabled',
                        'campaign_budget_enabled',
                        'campaign_max_total_spend_local',
                        'third_party_fees',
                        'budget_allocation_method',
                        'campaign_max_total_impressions',
                    ]);
                },
                format() {
                    return '';
                },
            },
            {
                label: 'Currency',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['currency']);
                },
                format(entry) {
                    return <>{entry.snapshot.currency}</>;
                },
                getClipboardText(entry) {
                    return entry.snapshot.currency;
                },
            },
            {
                label: 'Revenue Model',
                isCategory: false,
                hasChange(entry) {
                    const intersection = _.intersection(_.keys(entry.patch), ['revenueModel']);

                    return intersection.length > 0;
                },
                format(entry) {
                    const { snapshot } = entry;
                    let value = [];
                    if (snapshot.revenueModel === RevenueModelMapping.Disabled) {
                        value = [{ label: 'Disabled' }];
                    } else if (snapshot.revenueModel === RevenueModelMapping.CPMCPC) {
                        value = [{ label: 'CPM / CPC' }];
                    } else if (snapshot.revenueModel === RevenueModelMapping.CPM) {
                        value = [{ label: 'CPM' }];
                    } else if (snapshot.revenueModel === RevenueModelMapping.AgencyMargin) {
                        value = [{ label: 'Agency margin' }];
                    } else if (snapshot.revenueModel === RevenueModelMapping.TotalSpendMarkup) {
                        value = [{ label: 'Total cost markup' }];
                    }

                    return (
                        <div>
                            {_.map(value, item => (
                                <div key={item.label}>
                                    <span>{item.label}</span>
                                </div>
                            ))}
                        </div>
                    );
                },
            },
            {
                label: 'Total Cost Budget',
                isCategory: false,
                hasChange(entry) {
                    // this field is not used for guaranteed inventory type unless the revenue model is agencyMargin
                    if (
                        isUsingGuaranteedInventoryType(campaign) &&
                        entry.snapshot.revenueModel !== RevenueModelMapping.AgencyMargin
                    ) {
                        return false;
                    }

                    const intersection = _.intersection(_.keys(entry.patch), [
                        'billing_rate',
                        'billing_term',
                        'billing_enabled',
                        'campaign_budget_enabled',
                        'campaign_max_total_spend_local',
                    ]);

                    return intersection.length > 0;
                },
                format(entry) {
                    const value = getTotalCostBudgetLabels(entry.snapshot, campaign).map(label => ({
                        label,
                    }));

                    return (
                        <div>
                            {_.map(value, item => (
                                <div key={item.label}>
                                    <span>{item.label}</span>
                                </div>
                            ))}
                        </div>
                    );
                },
                useClipboardText: true,
                getClipboardText(entry) {
                    return getTotalCostBudgetLabels(entry.snapshot, campaign).join('\n');
                },
            },
            {
                label: 'Default Third Party Fees',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['third_party_fees']);
                },
                format(entry) {
                    return (
                        <div>
                            {_.map(entry.snapshot.third_party_fees, v => (
                                <div key={v.description}>
                                    {`${v.description}, ${formatCurrency(
                                        v.fee
                                    )} ${v.billing_model.toUpperCase()}`}
                                </div>
                            ))}
                        </div>
                    );
                },
                getClipboardText(entry) {
                    return _.map(
                        entry.snapshot.third_party_fees,
                        v =>
                            `${v.description}, ${formatCurrency(
                                v.fee
                            )} ${v.billing_model.toUpperCase()}`
                    ).join('\n');
                },
            },
            {
                label: 'Reference ID',
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['reference_id']);
                },
                format(entry) {
                    return <>{entry.snapshot.reference_id}</>;
                },
                getClipboardText(entry) {
                    return entry.snapshot.reference_id;
                },
            },
            {
                label: 'Restricted Content',
                isCategory: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['restricted_category']);
                },
                format() {
                    return '';
                },
            },
            {
                label: 'Restricted Category',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['restricted_category']);
                },
                format(entry) {
                    if (entry.snapshot.restricted_category === 'cannabis') {
                        return <>Cannabis</>;
                    }

                    return <>{entry.snapshot.restricted_category}</>;
                },
                getClipboardText(entry) {
                    if (entry.snapshot.restricted_category === 'cannabis') {
                        return 'Cannabis';
                    }

                    return entry.snapshot.restricted_category;
                },
            },
        ]
    );

    if (!canUseProgrammaticGuaranteedCampaigns()) {
        _.remove(fields, field => field.label === 'Inventory Type');
    }

    // this field is not used for guaranteed inventory type
    if (
        !isUsingGuaranteedInventoryType(campaign) &&
        (campaign.revenueModel === RevenueModelMapping.AgencyMargin ||
            campaign.revenueModel === RevenueModelMapping.Disabled)
    ) {
        fields.push({
            label: 'Budget Allocation Method',
            isCategory: false,
            hasChange(entry) {
                return hasTopLevelFieldsBeenModified(entry.patch, [
                    'budget_allocation_method',
                    'automaticBudgetAllocationOptimizationStrategy',
                ]);
            },
            format(entry) {
                const { snapshot } = entry;

                if (snapshot.budget_allocation_method === 'manual') {
                    return 'Manual';
                }

                let autoStrategyLabel = 'Automatic';
                if (
                    snapshot.automaticBudgetAllocationOptimizationStrategy ===
                    CampaignAutoBudgetAllocationOptions.CPC
                ) {
                    autoStrategyLabel += '(Optimize for CPC)';
                } else if (
                    snapshot.automaticBudgetAllocationOptimizationStrategy ===
                    CampaignAutoBudgetAllocationOptions.CPA
                ) {
                    autoStrategyLabel += '(Optimize for CPA)';
                } else if (
                    snapshot.automaticBudgetAllocationOptimizationStrategy ===
                    CampaignAutoBudgetAllocationOptions.CPCV
                ) {
                    autoStrategyLabel += '(Optimize for CPCV)';
                }

                return autoStrategyLabel;
            },
            getClipboardText(entry) {
                this.format(entry);
            },
        });
    }
    if (
        [RevenueModelMapping.CPMCPC, RevenueModelMapping.CPM].includes(campaign.revenueModel) &&
        campaign.budget_allocation_method === 'auto'
    ) {
        fields.push({
            label: 'Total Impressions',
            isCategory: false,
            hasChange(entry) {
                return hasTopLevelFieldsBeenModified(entry.patch, [
                    'campaign_max_total_impressions',
                ]);
            },
            format(entry) {
                return <>{entry.snapshot.campaign_max_total_impressions}</>;
            },
            getClipboardText(entry) {
                return entry.snapshot.campaign_max_total_impressions;
            },
        });
    }

    if (!isInternalUser()) {
        fields.push(
            {
                label: 'FTA',
                isCategory: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'fta_enabled',
                        'fta_management_level',
                        'fta_location_list',
                    ]);
                },
                format() {
                    return '';
                },
            },
            {
                label: 'Foot Traffic Attribution',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'fta_enabled',
                        'fta_management_level',
                        'fta_location_list',
                    ]);
                },
                format(entry) {
                    const { snapshot } = entry;
                    return (
                        <table>
                            <tbody>
                                <tr>
                                    <td>{snapshot.fta_enabled ? 'Enabled' : 'Disabled'}</td>
                                </tr>
                                {snapshot.fta_enabled && (
                                    <React.Fragment>
                                        <tr>
                                            <td>Level:</td>
                                            <td>
                                                {snapshot.fta_management_level === 'campaign'
                                                    ? 'Campaign'
                                                    : 'Ad'}
                                            </td>
                                        </tr>
                                        {snapshot.fta_management_level === 'campaign' && (
                                            <tr>
                                                <td>Location List:</td>
                                                <td>
                                                    {
                                                        ftaLocationListsMapping[
                                                            snapshot.fta_location_list
                                                        ]
                                                    }
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )}
                            </tbody>
                        </table>
                    );
                },
                getClipboardText(entry) {
                    return getCampaignFtaClipboardText(entry.snapshot, ftaLocationListsMapping);
                },
            }
        );
    }

    return fields;
}

export default function selector(storeState, props) {
    const campaignId = props.params.campaignId;
    const historyData = _.get(storeState, `resources.history.campaigns.${campaignId}`, []);
    const campaignAttr = _.get(storeState, `resources.campaigns.${campaignId}.attr`, {});

    const ftaLocationLists = _.get(storeState, 'campaignHistory.ftaLocationLists');

    const ftaLocationListsMapping = {};
    _.each(ftaLocationLists, locationList => {
        ftaLocationListsMapping[locationList.id] = locationList.name;
    });

    // to prevent duplicated consecutive history fields(only for valid field values);

    const fields = getFields({
        storeState,
        campaign: campaignAttr,
        ftaLocationListsMapping,
    });

    const filteredHistoryData = _(historyData)
        .filter((entry, index) => {
            if (index === 0) {
                return true;
            }

            const currenyHistoryValue = JSON.stringify(_.omit(entry.snapshot, ['_etag']));
            const prevHistoryValue = JSON.stringify(
                _.omit(historyData[index - 1].snapshot, ['_etag'])
            );

            return currenyHistoryValue !== prevHistoryValue;
        })
        .filter(entry => {
            const allChanges = _.map(fields, field => {
                return !!field.hasChange(entry);
            });

            const hasNoChanges = _.every(allChanges, change => change === false);

            return !hasNoChanges;
        })
        .value();

    const out = {
        isLoading: _.get(storeState, `campaignHistory.isLoading`, true),
        campaign: campaignAttr,
        fields,
        historyEntries: filteredHistoryData,
        organizationTimezone: storeState.profile.organizationTimezone,
    };
    return out;
}
